import Alert from '@mui/material/Alert';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { cardBody, cardUpdateBody, formatRate } from '@pulsepoint/shared';
import { CalendarClock, Pencil, Plus, Power, PowerOff, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { ApiError } from '../../api/client';
import { useAddCard, useDeleteCard, useUpdateCard, type Card, type DriverDetail } from '../../api/drivers';
import { usePermission } from '../../auth/permissions';
import { DataTable, type Column } from '../../components/DataTable';
import { useDialog, type DialogProps } from '../../components/dialogs/DialogProvider';
import { FormDialog } from '../../components/dialogs/FormDialog';
import { EmptyState } from '../../components/EmptyState';
import { applyApiError } from '../../components/form/apiErrors';
import { FormTextField } from '../../components/form/FormTextField';
import { RowMenu } from '../../components/RowMenu';
import { StatusLabel } from '../../components/StatusLabel';
import { useToast } from '../../components/Toast';
import { formSaver, useUnsavedChanges } from '../../components/unsaved/unsaved';
import { formatAgo, formatDate } from '../../lib/format';
import { UnknownCards } from './UnknownCards';

export const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 15 } as const;

export function cardStatus(c: Pick<Card, 'isActive' | 'isExpired'>) {
  if (!c.isActive) return <StatusLabel tone="off">Turned off</StatusLabel>;
  if (c.isExpired) return <StatusLabel tone="fault">Expired</StatusLabel>;
  return <StatusLabel tone="success">Works</StatusLabel>;
}

/** A driver's RFID cards: add, rename, set an expiry, turn off, or delete one that never charged. */
export function CardsPanel({ driver }: { driver: DriverDetail }) {
  const canEdit = usePermission('cards.edit') && driver.isActive;
  const update = useUpdateCard();
  const remove = useDeleteCard();
  const dialog = useDialog();
  const toast = useToast();

  const run = async (fn: () => Promise<unknown>, done: string) => {
    try {
      await fn();
      toast(done);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.', 'error');
    }
  };

  const columns: Column<Card>[] = [
    {
      key: 'tag',
      header: 'Card',
      sortValue: (c) => c.tag,
      render: (c) => (
        <Box>
          <Typography component="span" sx={mono}>{c.tag}</Typography>
          <Typography variant="body2" color="textSecondary">{c.label ?? ' '}</Typography>
        </Box>
      ),
    },
    { key: 'status', header: 'Status', render: (c) => cardStatus(c) },
    { key: 'rate', header: 'Rate', hideBelow: 'sm', render: (c) => (c.ratePerKwh == null ? <Typography color="textSecondary">Private price</Typography> : `${formatRate(c.ratePerKwh)} per kWh`) },
    { key: 'expires', header: 'Expires', hideBelow: 'sm', render: (c) => (c.expiresOn ? formatDate(`${c.expiresOn}T12:00:00`) : 'Never') },
    {
      key: 'used',
      header: 'Last used',
      hideBelow: 'sm',
      sortValue: (c) => c.lastUsedAt,
      render: (c) => (c.lastUsedAt ? `${formatAgo(c.lastUsedAt)} · ${c.sessionCount} session${c.sessionCount === 1 ? '' : 's'}` : <Typography color="textSecondary">Never</Typography>),
    },
  ];

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      {canEdit && <AddCard driver={driver} />}
      <DataTable
        label="Cards"
        rows={driver.cards}
        columns={columns}
        rowKey={(c) => c.id}
        pageSize={50}
        onRowClick={canEdit ? (c) => void dialog.open(CardDialog, { card: c }) : undefined}
        rowActions={
          canEdit
            ? (c) => (
                <RowMenu
                  label={`Actions for card ${c.tag}`}
                  items={[
                    { label: 'Edit', icon: Pencil, onClick: () => void dialog.open(CardDialog, { card: c }) },
                    { label: 'Turn off', icon: PowerOff, hidden: !c.isActive, onClick: () => void run(() => update.mutateAsync({ id: c.id, isActive: false }), `Card ${c.tag} is turned off. It won't start a session.`) },
                    { label: 'Turn on', icon: Power, hidden: c.isActive, onClick: () => void run(() => update.mutateAsync({ id: c.id, isActive: true }), `Card ${c.tag} works again.`) },
                    { label: 'Remove the expiry', icon: CalendarClock, hidden: !c.expiresOn, onClick: () => void run(() => update.mutateAsync({ id: c.id, expiresOn: null }), `Card ${c.tag} no longer expires.`) },
                    {
                      label: 'Delete',
                      icon: Trash2,
                      danger: true,
                      hidden: c.sessionCount > 0,
                      onClick: async () => {
                        const ok = await dialog.confirm({ title: `Delete card ${c.tag}?`, message: 'It has never been used, so nothing else changes.', confirmLabel: 'Delete', danger: true });
                        if (ok) await run(() => remove.mutateAsync(c.id), `Card ${c.tag} was deleted.`);
                      },
                    },
                  ]}
                />
              )
            : undefined
        }
        empty={<EmptyState title="No cards yet">{canEdit ? 'Add the number printed on the card or fob, or have the driver tap it on a charger first.' : 'This driver has no RFID cards.'}</EmptyState>}
      />
      {driver.cards.some((c) => c.sessionCount > 0) && canEdit && (
        <Typography variant="body2" color="textSecondary">Cards that have charged stay on record for reports. Turn them off instead of deleting them.</Typography>
      )}
    </Box>
  );
}

type AddIn = z.input<typeof cardBody>;
type AddOut = z.output<typeof cardBody>;

function AddCard({ driver }: { driver: DriverDetail }) {
  const add = useAddCard();
  const toast = useToast();
  const [error, setError] = useState('');
  const form = useForm<AddIn, unknown, AddOut>({ resolver: zodResolver(cardBody), defaultValues: { tag: '', label: '', expiresOn: '' } });

  const submit = formSaver(form, async (v) => {
    setError('');
    try {
      await add.mutateAsync({ driverId: driver.id, ...v });
      form.reset();
      toast(`Card ${v.tag} was added. It works straight away${driver.needsReview ? ' once the driver is approved' : ''}.`);
    } catch (e) {
      setError(applyApiError(e, form.setError, ['tag', 'label', 'expiresOn']));
      return false;
    }
  });
  useUnsavedChanges({ dirty: form.formState.isDirty, save: submit, discard: () => form.reset(), tab: true });

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {/* Not a <form>: this sits inside the driver form. Enter adds the card instead of saving the driver. */}
      <Box
        role="group"
        aria-label="Add a card"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void submit();
          }
        }}
        sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1.2fr 1fr auto' }, alignItems: 'start' }}
      >
        <FormTextField control={form.control} name="tag" label="Card number" placeholder="e.g. 04A2B3C4" slotProps={{ htmlInput: { autoCapitalize: 'characters', spellCheck: false, style: mono } }} />
        <FormTextField control={form.control} name="label" label="Label (optional)" placeholder="e.g. Blue key fob" />
        <Button variant="contained" startIcon={<Plus size={18} strokeWidth={1.75} />} disabled={add.isPending} onClick={() => void submit()} sx={{ mt: { sm: 0.5 } }}>
          Add
        </Button>
      </Box>
      {error && <Typography color="error" role="alert">{error}</Typography>}
      <UnknownCards organisationId={driver.organisationId} exclude={driver.cards.map((c) => c.tag)} onPick={(tag) => form.setValue('tag', tag, { shouldValidate: true })} />
    </Box>
  );
}

type EditIn = z.input<typeof cardUpdateBody>;
type EditOut = z.output<typeof cardUpdateBody>;

/**
 * A card: its label, an expiry date (it works all of that day) and, for admins, its own rate
 * instead of the charger's private price, e.g. $0.00 for a perk. Each behind a switch.
 */
function CardDialog({ card, close }: { card: Card } & DialogProps) {
  const update = useUpdateCard();
  const toast = useToast();
  const canSetRate = usePermission('tariffs.edit');
  const [error, setError] = useState('');
  const [expires, setExpires] = useState(!!card.expiresOn);
  const [ownRate, setOwnRate] = useState(card.ratePerKwh != null);
  const form = useForm<EditIn, unknown, EditOut>({
    resolver: zodResolver(cardUpdateBody),
    defaultValues: { label: card.label ?? '', expiresOn: card.expiresOn ?? '', ratePerKwh: card.ratePerKwh == null ? '' : card.ratePerKwh.toFixed(2) } as unknown as EditIn,
  });
  const submit = formSaver(form, async (v) => {
    setError('');
    if (expires && !v.expiresOn) {
      form.setError('expiresOn', { message: 'Choose the last day it works, or turn this off' });
      return false;
    }
    if (ownRate && (v.ratePerKwh === null || v.ratePerKwh === undefined)) {
      form.setError('ratePerKwh', { message: 'Enter the rate, e.g. 0.00 for free charging, or turn this off' });
      return false;
    }
    try {
      await update.mutateAsync({
        id: card.id,
        label: v.label,
        expiresOn: expires ? v.expiresOn : null,
        ...(canSetRate ? { ratePerKwh: ownRate ? v.ratePerKwh : null } : {}),
      });
      toast(`Card ${card.tag} was saved.`);
      close();
    } catch (e) {
      setError(applyApiError(e, form.setError, ['label', 'expiresOn', 'ratePerKwh']));
      return false;
    }
  });
  const changed = form.formState.isDirty || expires !== !!card.expiresOn || ownRate !== (card.ratePerKwh != null);
  useUnsavedChanges({ dirty: changed, save: submit, discard: () => form.reset() });
  return (
    <FormDialog title={`Card ${card.tag}`} submitLabel="Save" onSubmit={submit} onClose={() => close()} busy={update.isPending} error={error} maxWidth="xs">
      <Box sx={{ display: 'grid', gap: 2 }}>
        <FormTextField control={form.control} name="label" label="Label" placeholder="e.g. Blue key fob" autoFocus />

        <Box>
          <FormControlLabel control={<Switch checked={expires} onChange={(e) => setExpires(e.target.checked)} />} label="Set an expiry date" />
          {expires && (
            <Box sx={{ display: 'grid', gap: 1, mt: 1 }}>
              <FormTextField control={form.control} name="expiresOn" label="Expires" type="date" slotProps={{ inputLabel: { shrink: true } }} />
              <Typography variant="body2" color="textSecondary">
                The card works until the end of this day, then chargers refuse it. Handy for visitors, contractors or a driver who is leaving. Turn this off to make it work again.
              </Typography>
            </Box>
          )}
        </Box>

        {(canSetRate || card.ratePerKwh != null) && (
          <Box>
            <FormControlLabel control={<Switch checked={ownRate} onChange={(e) => setOwnRate(e.target.checked)} disabled={!canSetRate} />} label="Override the rate" />
            {ownRate ? (
              <Box sx={{ display: 'grid', gap: 1, mt: 1 }}>
                <FormTextField control={form.control} name="ratePerKwh" label="Rate ($ per kWh)" placeholder="e.g. 0.00" disabled={!canSetRate} slotProps={{ htmlInput: { inputMode: 'decimal' } }} />
                <Typography variant="body2" color="textSecondary">
                  Charged instead of each charger’s private price, at any time of day. $0.00 makes charging free with this card. Idle fees still apply.
                </Typography>
                <Alert severity="info" sx={{ mt: 1 }}>
                  Infinity EV’s rate, set in each charger’s private price, still applies to every kWh charged with this card, even at $0.00. The organisation pays it instead of the driver.
                </Alert>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ pl: { sm: 6 } }}>
                Off: the card is charged each charger’s private price.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </FormDialog>
  );
}
